import { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n";
import {
  getGaClientId, getAttributionContext, generateFormAttemptId,
  trackFormStarted, trackFormSubmitAttempted, trackFormSubmissionFailed, buildAttributionRequestContext,
} from "@/lib/analytics";
import { requestPlaybook } from "@/pages/PresenterArchetype/playbookApi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FORM_ID = "playbook_request_form";
const ASSET_ID = "next_presentation_playbook";
const ASSET_VARIANT = "generic";

/**
 * The public Guides page's own email-gated request form — reached
 * without having taken the Presenter Archetype quiz, so it requests
 * the *generic* six-page guide (no `answers` sent; page 3 shows all
 * four archetype cards). Same backend route/asset as the quiz
 * result's personalised `PlaybookCTA`, just without quiz answers.
 *
 * InterAct Tracking Foundation milestone (2026-10-09) — `form_started`/
 * `form_submit_attempted`/`form_submission_failed` via the SAME shared
 * `analytics.js` helpers `PlaybookCTA.jsx` uses, `asset_variant:
 * "generic"` (never `"personalized"` — this entry point has no quiz
 * run behind it) and NO `run_id` (never fabricated — there is no
 * diagnostic run for a visitor who skipped the quiz). `requestId`
 * below is the SEPARATE, pre-existing backend idempotency key (the
 * Mongo doc's own `_id`) — distinct from the NEW `attemptId` generated
 * per submit click for this tracking pair.
 */
export const GuideRequestForm = ({ ctaNavContext = null }) => {
  const { t, lang } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [state, setState] = useState("idle"); // idle | requesting | saved | error
  const [fieldError, setFieldError] = useState(null);
  const [gaClientId, setGaClientId] = useState(null);
  const attribution = getAttributionContext() || {};
  const formStartedRef = useRef(false);
  const formAttemptIdRef = useRef(null);

  useEffect(() => {
    getGaClientId().then(setGaClientId);
  }, []);

  const markFormStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    formAttemptIdRef.current = generateFormAttemptId();
    trackFormStarted({ formId: FORM_ID, assetId: ASSET_ID, assetVariant: ASSET_VARIANT, formAttemptId: formAttemptIdRef.current });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setFieldError(t("presenterPlaybookGuide.emailInvalid"));
      return;
    }
    setFieldError(null);
    setState("requesting");
    const attemptId = generateFormAttemptId();
    trackFormSubmitAttempted({ formId: FORM_ID, assetId: ASSET_ID, assetVariant: ASSET_VARIANT, formAttemptId: formAttemptIdRef.current, attemptId });
    try {
      await requestPlaybook({
        language: lang,
        email: email.trim(),
        name: name.trim() || null,
        marketing,
        gaClientId,
        requestId: crypto.randomUUID(),
        attributionContext: buildAttributionRequestContext({ ctaNavContext }),
      });
      setState("saved");
    } catch (err) {
      const errorCode = !err?.status ? "network_error" : err.status >= 500 ? "server_error" : err.status === 429 ? "rate_limited" : "validation_error";
      trackFormSubmissionFailed({ formId: FORM_ID, assetId: ASSET_ID, assetVariant: ASSET_VARIANT, formAttemptId: formAttemptIdRef.current, attemptId, errorCode });
      setState("error");
    }
  };

  if (state === "saved") {
    return (
      <div className="rounded-xl border border-[#607e66]/30 bg-[#607e66]/10 px-6 py-5" data-testid="guide-form-saved">
        <p className="font-body text-ink">{t("presenterPlaybookGuide.success")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="bg-paper border border-ink/12 p-7 md:p-8" data-testid="guide-request-form">
      <input type="hidden" name="ga_client_id" value={gaClientId || ""} data-testid="guide-form-ga-client-id" />
      <input type="hidden" name="gclid" value={attribution.gclid || ""} data-testid="guide-form-gclid" />
      <input type="hidden" name="oppref" value={attribution.oppref || ""} data-testid="guide-form-oppref" />
      <p className="font-serif text-xl text-ink mb-1.5">{t("presenterPlaybookGuide.formHeading")}</p>
      <p className="font-body text-sm text-ink-soft mb-5">{t("presenterPlaybookGuide.formBody")}</p>

      <input
        type="text"
        data-testid="guide-form-name-input"
        value={name}
        onChange={(e) => { markFormStarted(); setName(e.target.value); }}
        placeholder={t("presenterPlaybookGuide.namePlaceholder")}
        className="w-full border border-ink/15 px-4 py-3 text-sm text-ink mb-3 focus:outline-none focus:ring-2 focus:ring-terracotta"
      />
      <input
        type="email"
        data-testid="guide-form-email-input"
        value={email}
        onChange={(e) => { markFormStarted(); setEmail(e.target.value); }}
        placeholder={t("presenterPlaybookGuide.emailPlaceholder")}
        className="w-full border border-ink/15 px-4 py-3 text-sm text-ink mb-3 focus:outline-none focus:ring-2 focus:ring-terracotta"
      />

      <label className="flex items-start gap-2.5 text-sm text-ink-soft mb-4 cursor-pointer">
        <input
          type="checkbox"
          data-testid="guide-form-marketing-checkbox"
          checked={marketing}
          onChange={(e) => { markFormStarted(); setMarketing(e.target.checked); }}
          className="mt-0.5"
        />
        <span>{t("presenterPlaybookGuide.marketingLabel")}</span>
      </label>

      {fieldError && (
        <p className="text-sm text-terracotta mb-3" data-testid="guide-form-field-error" role="alert">
          {fieldError}
        </p>
      )}
      {state === "error" && (
        <p className="text-sm text-terracotta mb-3" data-testid="guide-form-request-error" role="alert">
          {t("presenterPlaybookGuide.error")}
        </p>
      )}

      <button
        type="submit"
        data-testid="guide-form-submit"
        disabled={state === "requesting"}
        className="w-full py-3 rounded-full font-semibold text-sm text-white bg-terracotta hover:bg-[#9c3f29] disabled:opacity-50 transition-colors"
      >
        {state === "requesting" ? t("presenterPlaybookGuide.preparing") : t("presenterPlaybookGuide.button")}
      </button>

      <p className="text-[11px] text-ink-soft/70 mt-3">{t("presenterPlaybookGuide.requestNotice")}</p>
      <p className="text-[11px] text-ink-soft/70 mt-1">{t("presenterPlaybookGuide.privacy")}</p>
    </form>
  );
};

export default GuideRequestForm;
